// Admin UI access is derived only from service-controlled GoTrue app_metadata.
// Destination APIs remain the authoritative security boundaries; these helpers
// keep the hub and route guards aligned on which affordances to show.
type ClaimValue = string | number | boolean | object | null | undefined

type AdminPrincipal = { app_metadata?: Record<string, ClaimValue> | null } | null | undefined

export interface AdminAccess {
  topup: boolean
  flags: boolean
  banners: boolean
  apiKeys: boolean
  builtinSkills: boolean
  internalModels: boolean
  onboarding: boolean
}

// Exported as a constant rather than inlined (unlike the older predicates
// above) so the drift test can assert the value this guard ACTUALLY
// dereferences equals dreamwright_api.deps.ONBOARDING_PERMISSION and the
// literal in scripts/grant_onboarding_permission.py. Comparing two hand-copied
// literals would only prove they matched the day the test was written.
// See tests/test_onboarding_permission_parity.py.
export const ONBOARDING_OPERATOR_PERMISSION = 'onboarding_operator'

export function hasBroadAdminRole(user: AdminPrincipal): boolean {
  return user?.app_metadata?.role === 'admin'
}

export function hasTopupOperatorPermission(user: AdminPrincipal): boolean {
  if (hasBroadAdminRole(user)) return true
  const permissions = user?.app_metadata?.permissions
  return Array.isArray(permissions) && permissions.includes('topup_operator')
}

export function hasFlagsAdminPermission(user: AdminPrincipal): boolean {
  if (hasBroadAdminRole(user)) return true
  const permissions = user?.app_metadata?.permissions
  return Array.isArray(permissions) && permissions.includes('flags_admin')
}

export function hasBannerAdmin(user: AdminPrincipal): boolean {
  return hasBroadAdminRole(user)
}

// Kept as a distinct predicate from hasBannerAdmin so /admin/api can be moved
// to a dedicated permission later without changing the banner gate.
export function hasApiKeysAdmin(user: AdminPrincipal): boolean {
  return hasBroadAdminRole(user)
}

export function hasInternalModelsAdmin(user: AdminPrincipal): boolean {
  return hasBroadAdminRole(user)
}

/**
 * Whether the principal may use the white-glove onboarding tool.
 *
 * Deliberately its own permission rather than reusing `topup_operator`: the
 * onboarding tool creates accounts, edits the registration allowlist and mails
 * external partners, which is materially more than minting credits.
 *
 * Reads service-controlled `app_metadata` only, and fails closed.
 */
export function hasOnboardingOperatorPermission(user: AdminPrincipal): boolean {
  if (hasBroadAdminRole(user)) return true
  const permissions = user?.app_metadata?.permissions
  return Array.isArray(permissions) && permissions.includes(ONBOARDING_OPERATOR_PERMISSION)
}

export function isAdminLocalDev(): boolean {
  return (
    import.meta.env.DEV &&
    !import.meta.env.VITE_ENABLE_AUTH &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  )
}

export function getAdminAccess(
  user: AdminPrincipal,
  localDevBypass = isAdminLocalDev(),
): AdminAccess {
  return {
    topup: localDevBypass || hasTopupOperatorPermission(user),
    flags: localDevBypass || hasFlagsAdminPermission(user),
    banners: localDevBypass || hasBannerAdmin(user),
    apiKeys: localDevBypass || hasApiKeysAdmin(user),
    builtinSkills: localDevBypass || hasBroadAdminRole(user),
    internalModels: localDevBypass || hasInternalModelsAdmin(user),
    onboarding: localDevBypass || hasOnboardingOperatorPermission(user),
  }
}

export function hasAnyAdminAccess(
  user: AdminPrincipal,
  localDevBypass = isAdminLocalDev(),
): boolean {
  const access = getAdminAccess(user, localDevBypass)
  return (
    access.topup ||
    access.flags ||
    access.banners ||
    access.apiKeys ||
    access.builtinSkills ||
    access.internalModels ||
    access.onboarding
  )
}
